import React from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  CircularProgress,
  Breakpoint,
} from "@mui/material";

interface CADialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: Breakpoint;
  fullWidth?: boolean;
  actions?: React.ReactNode;
  // Convenience props for common action patterns
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: "primary" | "secondary" | "success" | "error" | "warning" | "info";
  confirmDisabled?: boolean;
  isLoading?: boolean;
  showCloseButton?: boolean;
  dividers?: boolean;
}

export const CADialog: React.FC<CADialogProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = "sm",
  fullWidth = true,
  actions,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "primary",
  confirmDisabled = false,
  isLoading = false,
  showCloseButton = true,
  dividers = false,
}) => {
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose();
    }
  };

  // Render default actions if onConfirm is provided and no custom actions
  const renderActions = () => {
    if (actions) {
      return actions;
    }

    if (onConfirm) {
      return (
        <>
          <Button onClick={handleCancel} disabled={isLoading} sx={{ fontWeight: 600 }}>
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            color={confirmColor}
            disabled={confirmDisabled || isLoading}
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
            }}
          >
            {isLoading ? <CircularProgress size={20} color="inherit" /> : confirmText}
          </Button>
        </>
      );
    }

    return (
      <Button onClick={onClose} sx={{ fontWeight: 600 }}>
        Close
      </Button>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          border: "2px solid",
          borderColor: "grey.300",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          pt: 2.5,
          px: 3,
          borderBottom: dividers ? "1px solid" : "none",
          borderColor: "grey.200",
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            fontFamily: "ABC Whyte",
          }}
        >
          {title}
        </Typography>
        {showCloseButton && (
          <IconButton
            onClick={onClose}
            disabled={isLoading}
            size="small"
            sx={{
              color: "grey.500",
              "&:hover": {
                backgroundColor: "grey.100",
                color: "grey.700",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        dividers={dividers}
        sx={{
          px: 3,
          py: 2.5,
          "&.MuiDialogContent-dividers": {
            borderColor: "grey.200",
          },
        }}
      >
        <Box sx={{ pt: dividers ? 0 : 1 }}>{children}</Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "grey.200",
          gap: 1,
        }}
      >
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
};

export default CADialog;
